import { createLogic } from 'redux-logic'
import { LogicDeps, AppState } from '@chegg-wt/core/state'
import { FSACreatorPayload } from '@chegg-wt/core/state/types'
import { folderIdSelector } from '@chegg-wt/core/state/folders/selectors'

import { UpdateProjectVariables } from '@chegg-wt/core/queries/UpdateProject'
import { DeleteProjectsVariables } from '@chegg-wt/core/queries/DeleteProjects'
import {
  createCiteProjectSuccess,
  deleteCiteProjectError,
  createCiteProjectError,
} from '@chegg-wt/core/state/folders/actions'
import { ErrorMsgs } from '@chegg-wt/core/constants/errorMsgs'
import { ProjectTypeKeys } from '@chegg-wt/core/constants/projectTypeKey'
import { closeModal } from '../modals/actions'
import { DELETE_MODAL } from '../modals/constants'
import { currentProjectIdSelector } from '../currentProject/selectors'
import { clearCurrentProject } from '../currentProject/actions'
import { ActionTypes, sortNameToOrderByTypeMap } from './constants'
import {
  projectFilterSelector,
  projectsOrderSelector,
  projectsSortNameSelector,
  projectSearchSelector,
} from './selectors'
import {
  searchProjects,
  storeProjects,
  fetchProjectsError,
  sortProjects,
  sortProjectsError,
  starProjectError,
  unStarProjectError,
  fetchProjects,
  editCiteProjectError,
  setProjectFilter,
} from './actions'
import { StarProjectPayload, CreateProjectsPayload, FetchProjectsPayload } from './types'

export const getProjectsLogic = createLogic<
  AppState,
  FetchProjectsPayload,
  any,
  LogicDeps
>({
  type: ActionTypes.FETCH_PROJECTS,
  async process({ api, action, getState }) {
    try {
      const { limit } = action.payload
      const sortName = projectsSortNameSelector(getState())
      const order = projectsOrderSelector(getState())
      const orderBy = sortNameToOrderByTypeMap[sortName]
      const folderId = folderIdSelector(getState())

      const filter = projectFilterSelector(getState()) === 'star' ? 'star' : undefined
      const projects = folderId
        ? await api.bff.getProjectsByFolder({ filter, limit, order, orderBy, folderId })
        : await api.bff.getProjects({ filter, limit, order, orderBy })

      return storeProjects(projects)
    } catch (err) {
      return fetchProjectsError(new Error(ErrorMsgs.GET_PROJECTS_ERROR))
    }
  },
})

export const searchProjectsLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof searchProjects>,
  any,
  LogicDeps
>({
  type: ActionTypes.SEARCH_PROJECTS,
  async process({ api, action }, dispatch, done) {
    const { searchValue: name } = action.payload
    try {
      const projects = await api.bff.searchProjects({ name })
      if (!projects.length) {
        dispatch(setProjectFilter(ProjectTypeKeys.SEARCHING))
      }
      dispatch(storeProjects(projects))
      done()
    } catch (err) {
      dispatch(fetchProjectsError(new Error(ErrorMsgs.GET_PROJECTS_ERROR)))
      done()
    }
  },
})

export const sortProjectsLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sortProjects>,
  any,
  LogicDeps
>({
  type: ActionTypes.SORT_PROJECTS,
  async process({ api, action, getState }, dispatch, done) {
    const { orderBy, order } = action.payload
    try {
      const name = projectSearchSelector(getState())
      const folderId = folderIdSelector(getState())
      const filter = projectFilterSelector(getState()) === 'star' ? 'star' : undefined

      let projects
      if (name) {
        projects = await api.bff.searchProjects({ order, orderBy, name, filter })
      } else if (folderId) {
        projects = await api.bff.getProjectsByFolder({ order, orderBy, folderId, filter })
      } else {
        projects = await api.bff.getProjects({ order, orderBy, filter })
      }

      dispatch(storeProjects(projects))
      done()
    } catch (err) {
      dispatch(sortProjectsError(new Error(ErrorMsgs.GET_PROJECTS_ERROR)))
      done()
    }
  },
})

export const starProjectLogic = createLogic<AppState, StarProjectPayload, any, LogicDeps>(
  {
    type: ActionTypes.STAR_PROJECT,
    async process({ api, action }) {
      const { id } = action.payload
      try {
        await api.bff.starProject({ id, star: true })
      } catch (err) {
        return starProjectError(new Error(ErrorMsgs.GET_PROJECTS_ERROR))
      }
    },
  }
)

export const unStarProjectLogic = createLogic<
  AppState,
  StarProjectPayload,
  any,
  LogicDeps
>({
  type: ActionTypes.UNSTAR_PROJECT,
  async process({ api, action }) {
    const { id } = action.payload
    try {
      await api.bff.starProject({ id, star: false })
    } catch (err) {
      return unStarProjectError(new Error(ErrorMsgs.GET_PROJECTS_ERROR))
    }
  },
})

export const createProjectLogic = createLogic<
  AppState,
  CreateProjectsPayload,
  any,
  LogicDeps
>({
  type: ActionTypes.CREATE_PROJECT,
  async process({ api, action, getState }, dispatch, done) {
    try {
      const { name, defaultStyle } = action.payload
      const folderId = folderIdSelector(getState())
      await api.bff.createProject({
        name,
        defaultStyle,
        public: false, // default to false
        folderId,
      })
      dispatch(createCiteProjectSuccess())
      dispatch(fetchProjects())
    } catch (err) {
      dispatch(createCiteProjectError(new Error(ErrorMsgs.GET_PROJECTS_ERROR)))
    } finally {
      done()
    }
  },
})

export const editProjectLogic = createLogic<
  AppState,
  UpdateProjectVariables,
  any,
  LogicDeps
>({
  type: ActionTypes.EDIT_PROJECT,
  async process({ api, action }) {
    try {
      const project = action.payload
      await api.bff.editProject(project)
      return fetchProjects()
    } catch (err) {
      return editCiteProjectError(new Error(ErrorMsgs.GET_PROJECTS_ERROR))
    }
  },
})

export const batchDeleteProjectsLogic = createLogic<
  AppState,
  DeleteProjectsVariables,
  any,
  LogicDeps
>({
  type: ActionTypes.BATCH_DELETE_PROJECTS,
  async process({ api, action, getState }, dispatch, done) {
    try {
      const sortName = projectsSortNameSelector(getState())
      const order = projectsOrderSelector(getState())
      const orderBy = sortNameToOrderByTypeMap[sortName]
      const { ids } = action.payload
      const state = getState()

      await api.bff.deleteProjects({ ids })

      dispatch(fetchProjects({ order, orderBy }))
      dispatch(closeModal(DELETE_MODAL))

      // clean up current project
      const currentProjectId = currentProjectIdSelector(state)

      if (ids.includes(currentProjectId)) dispatch(clearCurrentProject())
    } catch (err) {
      dispatch(deleteCiteProjectError(new Error(ErrorMsgs.GET_PROJECTS_ERROR)))
    } finally {
      done()
    }
  },
})
